import { createApi, fetchBaseQuery, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { getApiConfig } from '@/config/api';
import { getRequestLanguage } from '@/utils/requestLanguage';
import type { Etalase, ApiResponse } from '@/types';

const dynamicEtalaseBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const config = getApiConfig();

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_URL,
    prepareHeaders: (headers, { getState }) => {
      let token = (getState() as any).auth.token;
      if (!token) {
        token = localStorage.getItem('token');
      }
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      headers.set('accept-language', getRequestLanguage());
      return headers;
    },
  });

  return baseQuery(args, api, extraOptions);
};

export const etalaseApi = createApi({
  reducerPath: 'etalaseApi',
  baseQuery: dynamicEtalaseBaseQuery,
  tagTypes: ['Etalase'],
  endpoints: (builder) => ({
    getEtalases: builder.query<ApiResponse<Etalase[]>, { outletId?: string; includeInactive?: boolean; includeDeleted?: boolean } | void>({
      query: (params) => ({
        url: '/etalases',
        params: params || undefined,
      }),
      providesTags: ['Etalase'],
    }),

    getEtalase: builder.query<ApiResponse<Etalase>, string>({
      query: (id) => `/etalases/${id}`,
      providesTags: (_, __, id) => [{ type: 'Etalase', id }],
    }),

    createEtalase: builder.mutation<
      ApiResponse<Etalase>,
      { outletId: string; name: string; description?: string; isActive?: boolean }
    >({
      query: (data) => ({
        url: '/etalases',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Etalase'],
    }),

    updateEtalase: builder.mutation<
      ApiResponse<Etalase>,
      { id: string; data: { name?: string; description?: string; isActive?: boolean } }
    >({
      query: ({ id, data }) => ({
        url: `/etalases/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Etalase', id }, 'Etalase'],
    }),

    deleteEtalase: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `/etalases/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Etalase'],
    }),
  }),
});

export const {
  useGetEtalasesQuery,
  useGetEtalaseQuery,
  useCreateEtalaseMutation,
  useUpdateEtalaseMutation,
  useDeleteEtalaseMutation,
} = etalaseApi;
